/**
 * Fullscreen is not one mechanism. Three environments need three strategies,
 * and the usual capability probe (`document.fullscreenEnabled`) LIES in the
 * one place it matters most:
 *
 *  - iPhone has no element fullscreen at all (Element.requestFullscreen is
 *    iPad-only). A `<video>` can still go fullscreen through the legacy
 *    `webkitEnterFullscreen`, which hands playback to Apple's native chrome —
 *    our control bar disappears while it is up, by design.
 *  - A stock Android WebView OVERRIDES `onShowCustomView` and calls
 *    `onCustomViewHidden()` immediately, so every `requestFullscreen()` is
 *    cancelled on arrival — while `fullscreenEnabled` still reports true
 *    because the method is overridden at all. We fake it with a fixed-position
 *    wrapper instead, which also keeps the inline speed menu and up-next card
 *    working.
 *  - Our OWN Capacitor shell now ships a WebChromeClient that honours the
 *    request (Phase 9), so it TRIES element fullscreen and lets the verification
 *    below decide. That is what keeps an older APK correct: one built before
 *    that change still cancels the request, the check notices, and it lands on
 *    the CSS fallback exactly as it did before.
 *  - Everywhere else the standard element API works.
 */
export type FullscreenMode = 'element' | 'video-native' | 'css';

export type FullscreenEnv = {
  fullscreenEnabled: boolean;
  /** `typeof video.webkitEnterFullscreen === 'function'` */
  hasVideoNativeFullscreen: boolean;
  /** `window.Capacitor?.isNativePlatform?.() === true` */
  isNativeShell: boolean;
  userAgent: string;
};

/** Pure decision half — unit tested; the DOM half below is not testable here. */
export function resolveFullscreenMode(env: FullscreenEnv): FullscreenMode {
  // iPhone first: the strictest signal, and true inside a native shell too.
  if (!env.fullscreenEnabled && env.hasVideoNativeFullscreen) return 'video-native';
  // Our own shell handles the custom view, so ATTEMPT the real thing — and note
  // this must come BEFORE the UA test below, because the shell's WebView user
  // agent also contains `; wv)`. `enterFullscreen` verifies and degrades on an
  // APK that predates that support.
  if (env.isNativeShell) return 'element';
  // Any other Android WebView claims support and cancels it.
  if (/ wv[);]/.test(env.userAgent)) return 'css';
  return 'element';
}

type WebkitVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
};

export function readFullscreenEnv(video: HTMLVideoElement | null): FullscreenEnv {
  const shell = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
    .Capacitor;
  return {
    fullscreenEnabled: typeof document !== 'undefined' && !!document.fullscreenEnabled,
    hasVideoNativeFullscreen:
      typeof (video as WebkitVideo | null)?.webkitEnterFullscreen === 'function',
    isNativeShell: shell?.isNativePlatform?.() === true,
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
  };
}

export const PSEUDO_FULLSCREEN_ATTR = 'data-pseudo-fullscreen';

/**
 * Enter fullscreen and report the mode that ACTUALLY took effect, so the
 * button icon can never claim a state the browser refused. `element` verifies
 * itself on the next frame and degrades to `css` when the request was
 * swallowed (the Capacitor case above).
 */
export async function enterFullscreen(
  wrapper: HTMLElement,
  video: HTMLVideoElement | null,
  mode: FullscreenMode,
): Promise<FullscreenMode | null> {
  if (mode === 'video-native') {
    (video as WebkitVideo | null)?.webkitEnterFullscreen?.();
    return 'video-native';
  }
  if (mode === 'element') {
    try {
      await wrapper.requestFullscreen();
      // Wait for the browser to SAY it happened, not just for a frame to pass.
      // A native shell's custom view brings an orientation change with it and
      // can take well over one frame; resolving early would leave us in real
      // fullscreen AND wearing the CSS fallback at the same time.
      const ok = await new Promise<boolean>((resolve) => {
        if (document.fullscreenElement === wrapper) return resolve(true);
        let settled = false;
        const finish = (value: boolean) => {
          if (settled) return;
          settled = true;
          document.removeEventListener('fullscreenchange', onChange);
          clearTimeout(timer);
          resolve(value);
        };
        const onChange = () => finish(document.fullscreenElement === wrapper);
        document.addEventListener('fullscreenchange', onChange);
        const timer = setTimeout(() => finish(document.fullscreenElement === wrapper), 500);
      });
      if (ok) return 'element';
    } catch {
      /* fall through to the CSS fallback */
    }
  }
  wrapper.setAttribute(PSEUDO_FULLSCREEN_ATTR, 'true');
  return 'css';
}

export async function exitFullscreen(wrapper: HTMLElement, mode: FullscreenMode): Promise<void> {
  if (mode === 'css') {
    wrapper.removeAttribute(PSEUDO_FULLSCREEN_ATTR);
    return;
  }
  if (mode === 'element' && document.fullscreenElement) {
    try {
      await document.exitFullscreen();
    } catch {
      /* already gone */
    }
  }
}
